import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

const SLUG = "kiipeilyteline";
const H1 = "Lasten kiipeilyteline sisälle — Pikler-kolmio, kiipeilykaari & leikkiteline";
const TITLE = "Kiipeilyteline lapselle sisälle — Pikler & kaari | Montessori24";
const DESC =
  "Pikler-kolmiot, kiipeilykaaret ja rampit sisäkäyttöön. Vertaa mittoja, ikäsuosituksia ja ominaisuuksia — ilmainen toimitus Suomeen 3–7 pv.";

const FAQS = [
  {
    q: "Minkä ikäiselle lasten kiipeilyteline sisälle sopii?",
    a: "Ikäsuositukset vaihtelevat tuotteen ja valmistajan mukaan. Tarkista aina tuotekohtainen ikäsuositus ja enimmäiskuormitus tuotteen omalta sivulta — ne ovat ensisijaista tietoa.",
  },
  {
    q: "Onko lasten kiipeilyteline sisälle oikeasti turvallinen?",
    a: "Kyllä, kun teline on kestävä, sen alla on pehmeä matto (esim. leikkimatto tai jumppapatja) ja lapsi kiipeilee valvotusti. Tarkista tuotekohtainen ikäsuositus, enimmäiskuormitus ja turvallisuustiedot tuotteen omalta sivulta.",
  },
  {
    q: "Paljonko sisäkiipeilyteline vie tilaa?",
    a: "Pikler-kolmio noin 80×80 cm, kaari 90×60 cm. Monet mallit taittuvat kasaan säilytystä varten, jolloin ne mahtuvat sängyn alle tai kaappiin.",
  },
  {
    q: "Kiipeilyteline sisälle vai pihalle?",
    a: "Valikoimamme kiipeilytelineet on tarkoitettu sisäkäyttöön: vaneri ei kestä ympärivuotista säänvaihtelua ilman erillistä huoltoa. Tarkista käyttöympäristö tuotesivulta. Pihalle tai ulos etsi säänkestävästä puusta valmistettu ulkoteline.",
  },
  {
    q: "Mitä eroa on kiipeilytelineellä, leikkitelineellä ja leikkitornilla?",
    a: "Käytännössä samaa asiaa eri nimillä. Kiipeilyteline korostaa kiipeilyä, leikkiteline yleistä leikkiä ja leikkitorni korkeutta. Valikoimamme kiipeilytelineitä yhdistelemällä (kolmio + kaari + ramppi) saat kaikki kolme yhdessä setissä.",
  },
  {
    q: "Onko teillä kiipeilyseinää tai kiipeilyverkkoa kotiin?",
    a: "Suoranaista kiipeilyseinää tai kiipeilyverkkoa emme myy, mutta Pikler-kolmion, kiipeilykaaren ja rampin yhdistelmä tarjoaa samat karkeamotoriset harjoitukset kotona ilman että seinään täytyy porata. Setti on siirreltävä ja säilytettävissä taitettuna.",
  },
];

export const Route = createFileRoute("/kiipeilyteline")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: KiipeilytelinePage,
});

function KiipeilytelinePage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Lasten kiipeilyteline sisälle"
      title={H1}
      intro="Pikler-kolmiot, kiipeilykaaret ja rampit yhdistyvät liikuntaradoiksi, jotka kasvavat lapsen mukana. Ikäsuositus vaihtelee tuotteen ja valmistajan mukaan — tarkista aina tuotekohtainen ikäsuositus ja turvallisuusohjeet tuotteen omalta sivulta."
      kauppaCategory="climbing"
      match={(t) => /kiipeil|kiipeily|kolmio|kaari|kolmikulm|3-?kulm|ramp|climbing|climber|triangle|arch|3-?angle|pikler/i.test(t)}
      relatedChips={[
        { label: "Pikler-kolmio", to: "/pikler-kolmio" },
        { label: "Oppimistornit", to: "/oppimistorni" },
        { label: "Oppimistorni-opas", to: "/oppimistorni-opas" },
        { label: "IGLU Blocks", to: "/iglu" },
      ]}
      body={[
        {
          heading: "Miksi lasten kiipeilyteline sisälle",
          paragraphs: [
            "Suomalainen sää tarkoittaa, että puolet vuodesta ulkoleikki on rajallista. Lasten kiipeilyteline sisälle antaa lapselle mahdollisuuden purkaa energiaa, harjoitella tasapainoa ja karkeamotoriikkaa myös silloin kun sadetakki on kuivumassa. Tutkimusten mukaan lapsi tarvitsee vähintään 3 tuntia liikuntaa päivässä — sisäkiipeilyteline tekee tästä helposti mahdollista.",
            "Sisäteline on myös turvallisempi tapa opetella kiipeilyä kuin sohva tai kirjahyllyt. Kiipeilyteline lapsille on suunniteltu kiipeilyyn: puolat oikeassa välissä, tukevat kaiteet, ei teräviä kulmia. Lapsen sohvakiipeilyn väheneminen on tavallinen sivuvaikutus.",
          ],
        },
        {
          heading: "Pikler-kolmio, kiipeilykaari, ramppi ja leikkitorni — mikä setti kannattaa",
          paragraphs: [
            "Aloita kolmiolla, jos lapsi on jo yli 1-vuotias. Se tarjoaa monta käyttöasentoa ja kasvaa lapsen mukana pitkään.",
            "Kaari on paras alle 1-vuotiaalle. Se on matala, pyöreä ja toimii myös keinuna sekä majana.",
            "Ramppi kannattaa lisätä siinä vaiheessa, kun lapsi kiipeää jo itsenäisesti — tarkista tuotekohtainen ikäsuositus tuotesivulta. Kolmio + ramppi = liukumäki + kiipeilypuola samassa — käytännössä pieni leikkitorni omaan olohuoneeseen.",
          ],
        },
        {
          heading: "Leikkiteline vai kiipeilyteline lapsille — mitä eroa nimillä on?",
          paragraphs: [
            "Suomeksi lasten liikuntavälineitä kutsutaan monella nimellä: kiipeilyteline, leikkiteline, leikkitorni, taaperoteline. Käytännössä ne tarkoittavat samaa: lapsen kokoinen sisäteline, jossa yhdistyvät kiipeily, tasapainoilu ja mielikuvitusleikki. Meidän valikoimassamme sama Pikler-yhdistelmä toimii kaikkina — kolmio + kaari + ramppi = kiipeilyteline, leikkiteline ja leikkitorni yhdessä.",
            "Jos etsit kiipeilyseinää kotiin tai kiipeilyverkkoa lapsille, siirreltävä Pikler-setti on usein järkevämpi valinta: ei porausta seinään, siirtyy huoneesta toiseen ja taittuu kasaan kun tarvitaan lisää lattiatilaa.",
          ],
        },
      ]}
      faqs={FAQS}
    />
  );
}